import { _initialDepartments } from '../../../fixtures/departmentFixtures';
import { _initialProjectPhase } from '../../../fixtures/projectFixtures';
import { IPagination } from '../../../types/pagination';
import { statusColor } from '../../../utils/statusColor';
import { IEmployee } from '../../settings/types/employee';
import { IProjectActivity, IProjectActivityHistory, IProjectActivitySortColumn, IProjectActivitySorting } from '..//types/projectActivity';
import { ActivitiesActions } from '../actions/activitiesActions';
import * as constants from '../actions/constants';
import { IActivityFilter, IActivitySearch } from '../types/activityFilters';
import { IProjectLabel } from './../types/project';


export const initialSort: IProjectActivitySorting = {
  clustering: 'phase',
  column: {name: 'name', direction: 'asc'} as IProjectActivitySortColumn,
}

export const _initialActivity: IProjectActivity = {
  id: '',
  name: '',
  phase: _initialProjectPhase[0],
  department: _initialDepartments[0],
  start_date: '',
  end_date: '',
  current_hours: 0,
  total_hours: 0,
  value: 0,
  percentage: '',
  status: 'upcoming',
  statusColor: (status: string) => statusColor(status),
  focused: false,
  remainingHours: '',
  remainingHoursPercentage: '',
  time_completion: 0,
  description: null,
  assigned_employees: [] as IEmployee[],
  labels: [] as IProjectLabel[],
  last_start_date: '',
  last_end_date: '',
}

const _initialActivityPagination: IPagination<IProjectActivity[]> = {
  links: { next: '', previous: '' },
  count: 0,
  current_page: 0,
  total_pages: 0,
  results: [_initialActivity],
  page_size: 5,
  last_page: 0,
  to: 0,
  from: 0
}

export const activities = (state: IProjectActivity[] = [], action: ActivitiesActions): IProjectActivity[] => {
  switch (action.type) {
    case constants.GET_ACTIVITIES_SUCCESS:
    case constants.PROJECT_LABELS_MERGE_TASKS_SUCCESS:
      return action.activities

    case constants.CREATE_ACTIVITY_SUCCESS:
      return [...state, action.activity]

    case constants.UPDATE_ACTIVITY_SUCCESS:
    case constants.GET_ACTIVITY_SUCCESS:
    case constants.SET_SELECTED_ACTIVITY:
      return state.map(a => {
        if (+a.id === +action.activity.id) {
          return action.activity
        }
        return a
      })

    case constants.DELETE_ACTIVITY_SUCCESS:
      return state.filter(a => a.id !== action.activity.id)

    default:
      return state
  }
}

export const searchActivities = (state = {search: ''}, action: ActivitiesActions): IActivitySearch => {
  switch (action.type) {
    case constants.SEARCH_ACTIVITIES:
      return action.filter

    default:
      return state
  }
}

export const sortActs = (state: IProjectActivitySorting = initialSort, action: ActivitiesActions): IProjectActivitySorting => {
  switch (action.type) {
    case constants.SORT_ACTS:
      return action.sorting

    default:
      return state
  }
}

export const sortActsInDanger = (state: IProjectActivitySorting = initialSort, action: ActivitiesActions): IProjectActivitySorting => {
  switch (action.type) {
    case constants.SORT_ACTS_IN_DANGER:
      return action.sorting

    default:
      return state
  }
}

export const activity = (state: IProjectActivity = _initialActivity, action: ActivitiesActions): IProjectActivity => {
  switch (action.type) {
    case constants.UPDATE_ACTIVITY_SUCCESS:
    case constants.CREATE_ACTIVITY_SUCCESS:
    case constants.GET_ACTIVITY_SUCCESS:
    case constants.SET_SELECTED_ACTIVITY:
      return action.activity

    default:
      return state
  }
}

export const activityHistory = (state: IProjectActivityHistory[] = [], action: ActivitiesActions): IProjectActivityHistory[] => {
  switch (action.type) {
    case constants.GET_ACT_HISTORY_SUCCESS:
      return action.history.sort((a, b) =>  b.history_id - a.history_id)

    case constants.RESET_ACT_HISTORY:
      return []

    default:
      return state
  }
}

export const actFilters = (state: IActivityFilter[] = [] as IActivityFilter[], action: ActivitiesActions): IActivityFilter[] => {
  switch(action.type) {
    case constants.GET_ACT_FILTERS_SUCCESS:
      return action.filters

    case constants.SET_ACT_FILTERS: {
      const { actFilter, queryOptions } = action
      return state.map(f => {
        if (f.name === actFilter.name) {
          const idx = f.options.findIndex(o => o.name === queryOptions.name)
          if (idx !== -1) {
            if (f.name === 'Due Date' || f.name === 'Status') {
              f.options = f.options.map((o, i) => ({...o, 'selected': i !== idx ? false : o.selected}))
              if (f.options[idx].value === 'individual') {
                f.options[idx].start_date = queryOptions.start_date
                f.options[idx].end_date = queryOptions.end_date
              }
            }
            f.options[idx].selected = f.options[idx].start_date || f.options[idx].end_date ? true : !f.options[idx].selected
          }
        }
        return f
      })
    }

    case constants.CLEAR_ACT_FILTERS: {
      const b =  state.map(f => {
        const _o = f.options.map(o => {
          return {...o, selected: false}
        })
        return {...f, options: _o}
      })

      return b
    }

    default:
      return state
  }
}

export const selectedActivity = (state: IProjectActivity = _initialActivity, action: ActivitiesActions): IProjectActivity => {
  switch (action.type) {
    case constants.UPDATE_ACTIVITY_SUCCESS:
    case constants.CREATE_ACTIVITY_SUCCESS:
    case constants.GET_ACTIVITY_SUCCESS:
    case constants.SET_SELECTED_ACTIVITY:
      return {...action.activity, statusColor: (status: string) => statusColor(status)}

    case constants.DELETE_ACTIVITY_SUCCESS:
      return _initialActivity

    default:
      return state
  }
}

export const openActDetailsModal = (state = false, action: ActivitiesActions): boolean => {
  switch (action.type) {
    case constants.SET_OPEN_ACT_DETAILS_MODAL:
      return action.open

    default:
      return state
  }
}

export const draggableIndicatorAct = (state = false, action: ActivitiesActions): boolean => {
  switch (action.type) {
    case constants.SET_DRAGGABLE_INDICATOR_ACT_SUCCESS:
      return action.draggable

    default:
      return state
  }
}

export const paginatedActivities = (state: IPagination<IProjectActivity[]> = _initialActivityPagination, action: ActivitiesActions): IPagination<IProjectActivity[]> => {
  switch (action.type) {
    case constants.GET_PAGINATED_ACTIVITIES_SUCCESS:
      return action.activities

    default:
      return state
  }
}

export const paginatedActivitiesActivePage = (state = 1, action: ActivitiesActions): number => {
  switch (action.type) {
    case constants.SET_PAGINATED_ACTIVITIES_ACTIVE_PAGE:
      return action.page

    default:
      return state
  }
}
